import { GetOptions } from "./../repositories/lineInfo/lineInfoRepository.types";
import { ILineFare } from "./../domain/entities/lineInfo/lineInfo.types";
import { IResultData } from "@/domain/entities/shared/result";
import {
  IBusLocation,
  ILineDistrict,
  ILineType,
  ILineInfo,
  ILineInfoLite,
  IBookInterval,
  ITurn,
  IContact,
  IFullSeatLine,
} from "@/domain/entities/lineInfo";
import { LineInfo } from "@/domain/entities/lineInfo/lineInfo";
import { defineStore } from "pinia";
import { IBusStop } from "@/domain/entities/busStop";
import {
  FavoriteLine,
  IFavoriteLine,
  IFavoriteLineData,
} from "@/domain/entities/favoriteLine";
import { container } from "@/ioc/inversify.config";
import {
  ILineInfoRepository,
  OptionMapper,
} from "@/repositories/lineInfo/lineInfoRepository.types";
import TYPES from "@/ioc/identifiers";
import {
  ArrivalTimeOptionCode,
  ArrivalTimeOptionCodeType,
  BookRequiredType,
  LineTypeCode,
  LineTypeName,
} from "@/types";
import useSetResultCodeIfNeeded from "@/use/useSetResultCodeIfNeeded";
import {
  IOnBoardLine,
  IOnBoardLineData,
} from "@/domain/entities/onBoardLine/onBoardLine.types";
import {
  FilterKeyValueType,
  IFilter,
  ILineSearchFilter,
  IOption,
} from "@/domain/entities/filter/filter.types";
import { IBusStopLayoutInfo } from "@/domain/entities/lineInfo/busStopLayout.types";
import { ILineFareUseCase } from "@/domain/usecases/line/interface/ILineFareUseCase";
import { GetBookIntervalsUseCase } from "@/domain/usecases/line/GetBookIntervals";
import { IArrivalTime } from "@/domain/entities/lineInfo/arrivalTime.types";
import { IFavoriteBusStop } from "@/domain/entities/favoriteBusStop";
import { IRoadEventsCollection } from "@/domain/entities/roadEvent";
import { useServiceStore } from "./serviceStore";
import { compressToUTF16, decompressFromUTF16 } from "lz-string";
import { sortArrayByString } from "@/utils/sortUtils";

interface ILineInfoState {
  /** 노선 정보 리스트 */
  lineInfos: ILineInfo[];
  /** 경량 노선 정보 리스트 */
  lineInfosLite: ILineInfoLite[];
  /** 사용자가 클릭한 노선 상세 */
  selectedLineInfoDetail: ILineInfo | undefined;
  /** 검색된 노선 정보 리스트 */
  searchedLineInfos: ILineInfo[];
  /** 노선 회차 리스트 */
  turns: ITurn[];
  /** 검색된 노선 회차 리스트 */
  searchedTurns: ITurn[];
  /** 노선 종류 (출근 / 퇴근 / 셔틀) */
  lineTypes: ILineType[];
  /** 노선 권역 (강남 / 강북 etc..) */
  lineDistricts: ILineDistrict[];
  /** 모든 버스 정류장 리스트 */
  busStops: IBusStop[];
  /** 내 주변 정류장 리스트 */
  nearBusStops: IBusStop[];
  /** 즐겨찾기 노선 리스트 */
  favoriteLines: IFavoriteLine[];
  /** 메인페이지 카드의 즐겨찾기 노선이 현재 운행중인지 여부 */
  isBusRunning: boolean;
  /** 특정 노선의 버스 위치들 (1개의 노선은 복수의 출발시간을 갖을 수 있음) */
  busLocations: IBusLocation[] | undefined;
  bookIntervals: IBookInterval[];
  /** 즐겨찾기한 정류장 목록 */
  favoriteBusStops: IFavoriteBusStop[];
  /** 진행 중인 getAllLineInfos 요청 */
  pendingGetAllLineInfosRequest: Promise<IResultData<ILineInfo[]>> | null;
  /** 진행 중인 getAllLineInfosLite 요청 */
  pendingGetAllLineInfosLiteRequest: Promise<
    IResultData<ILineInfoLite[]>
  > | null;
  /** 버스 정류장 ID에 따른 노선 정보 매핑 */
  busStopIdToLineInfos: Map<string, ILineInfoLite[]>;
}

const lineInfoRepository = () =>
  container.get<ILineInfoRepository>(TYPES.ILineInfoRepository);

const lineFareUseCase = () =>
  container.get<ILineFareUseCase>(TYPES.ILineFareUseCase);

const getBookIntervals = () =>
  container.get<GetBookIntervalsUseCase>(TYPES.GetBookIntervalsUseCase);

// 캐시 TTL을 계산하는 헬퍼 함수
function getCacheTtlMillis(): number {
  const serviceStore = useServiceStore();
  const ttlMinutes = serviceStore.serviceInfo?.lineCacheTtlMin ?? 30; // 기본값 30분
  return ttlMinutes * 60 * 1000; // 분을 밀리초로 변환
}

// 로컬스토리지에서 압축된 캐시 데이터를 가져와서 압축 해제하는 헬퍼 함수
function getDecompressedCacheData(cacheKey: string): string | null {
  try {
    const compressed = localStorage.getItem(cacheKey);
    if (!compressed) return null;

    const decompressed = decompressFromUTF16(compressed);
    return decompressed;
  } catch (error) {
    console.error("캐시 데이터 압축 해제 오류:", error);
    return null;
  }
}

function busStopExistsWithKeyword(
  lineInfo: ILineInfo,
  keyword: string
): boolean {
  return !!lineInfo.turns[0].busStops.find((busStop) => {
    return busStop.name.includes(keyword);
  });
}

function lineInfoExistsWithKeyword(
  lineInfo: ILineInfo,
  keyword: string
): boolean {
  return (
    lineInfo.lineNum.includes(keyword) ||
    lineInfo.routeName.includes(keyword) ||
    lineInfo.lineTypeName.includes(keyword.toUpperCase())
  );
}

function busStopExistsWithId(lineInfo: ILineInfo, id: string): boolean {
  return !!lineInfo.turns[0].busStops.find((busStop) => {
    return busStop.id === id;
  });
}

function parseBusStop(lineInfos: ILineInfo[]): IBusStop[] {
  const busStops: IBusStop[] = [];
  lineInfos.forEach((lineInfo) => {
    lineInfo.turns.forEach((turn) => {
      turn.busStops.forEach((busStop) => {
        const exist = busStops.some(
          (busStopData) => busStopData.id === busStop.id
        );
        if (!exist) {
          busStops.push(busStop);
        }
      });
    });
  });
  return busStops;
}

const parseOnBoardToFavorite = (
  onBoardLine: IOnBoardLine,
  favoriteLines: IFavoriteLine[]
): FavoriteLine => {
  let turnId = onBoardLine.lineTurnUid;
  // 사내셔틀인 경우 회차 선택이 없기 때문에 UI 일관성 유지를 위해 기존 즐겨찾기 노선의 turnId 로 덮어쓴다.
  if (onBoardLine.lineTypeCode === LineTypeCode.Inside) {
    const sameRouteLine = favoriteLines.find(
      (favoriteLine) => favoriteLine.lineId === onBoardLine.lineRouteUid
    );
    turnId = sameRouteLine?.turnId ?? onBoardLine.lineTurnUid;
  }

  const onBoardFavoriteLineData: IFavoriteLineData = {
    lineId: onBoardLine.lineRouteUid,
    turnId,
    lineNum: onBoardLine.lineRouteNo,
    plateNum: onBoardLine.carNo,
    lineStartTime: onBoardLine.firstBusStop?.arrivalTime,
    routeName: onBoardLine.lineRouteName,
    lineTypeCode: onBoardLine.lineTypeCode as LineTypeCode,
    lineTypeName: onBoardLine.lineTypeName as LineTypeName,
    busStops: onBoardLine.busStops,
    getOnBusStop: onBoardLine.getOnBusStop, // 비예약일 경우 undefined, 예약일 경우 예상 승차 정류장이 들어옴
    getOffBusStop: onBoardLine.getOffBusStop, // 비예약일 경우 undefined, 예약일 경우 예상 하차 정류장이 들어옴
    firstBusStop: onBoardLine.firstBusStop,
    lastBusStop: onBoardLine.lastBusStop,
    getOnNotice: false, // 정류장의 알림상태 값 사용하기 때문에 false
    getOffNotice: false, // 정류장의 알림상태 값 사용하기 때문에 false
    monthlyBookChecked: false, // 탑승중인 경우 예약버튼 없으므로 false
    lineFare: {} as ILineFare,
    earlyDriveInfos: onBoardLine.earlyDriveInfos,
    lineSets: onBoardLine.lineSets,
    isBoarding: true,
    boardUid: onBoardLine.boardUid,
    boardedCarNo: onBoardLine.carNo,
    requestGetOffBusStopId: onBoardLine.requestGetOffBusStopId,
    isTempOnboardFavorite: true,
    detourBeginDtm: onBoardLine.detourBeginDtm,
    detourEndDtm: onBoardLine.detourEndDtm,
    detourReason: onBoardLine.detourReason,
  };

  return new FavoriteLine(onBoardFavoriteLineData);
};

export const useLineStore = defineStore("lines", {
  state: (): ILineInfoState => {
    return {
      lineInfos: [],
      lineInfosLite: [],
      selectedLineInfoDetail: undefined,
      searchedLineInfos: [],
      turns: [],
      searchedTurns: [],
      lineTypes: [],
      lineDistricts: [],
      busStops: [],
      nearBusStops: [],
      favoriteLines: [],
      isBusRunning: false,
      busLocations: [],
      bookIntervals: [],
      favoriteBusStops: [],
      pendingGetAllLineInfosRequest: null,
      pendingGetAllLineInfosLiteRequest: null,
      busStopIdToLineInfos: new Map(),
    };
  },
  getters: {
    getLineInfosByKeyword(state) {
      return (keyword: string) => {
        if (keyword.length <= 0) {
          return state.lineInfos;
        }
        return state.lineInfos.filter((lineInfo) => {
          return (
            lineInfoExistsWithKeyword(lineInfo, keyword) ||
            busStopExistsWithKeyword(lineInfo, keyword)
          );
        });
      };
    },
    getLineInfosByBusStopId(state) {
      return (id: string) => {
        return state.lineInfos.filter((lineInfo) => {
          return busStopExistsWithId(lineInfo, id);
        });
      };
    },
    getLineInfosLiteByBusStopId(state) {
      return (id: string) => {
        return state.lineInfosLite.filter((lineInfoLite) => {
          return lineInfoLite.turns.some((turn) =>
            turn.stations?.some((station) => station.id === id)
          );
        });
      };
    },
    getLineInfosByLineType(state) {
      return (lineTypeCode: LineTypeCode) => {
        return state.lineInfos.filter((lineInfo) => {
          return lineInfo.lineTypeCode === lineTypeCode;
        });
      };
    },
    getLineInfoByLineId(state) {
      return (id: string) => {
        return state.lineInfos.find((lineInfo) => {
          return lineInfo.id === id;
        });
      };
    },
    getLineInfoByTurnId(state) {
      return (id: string) => {
        return state.lineInfos.find((lineInfo) => {
          return !!lineInfo.turns.find((turn) => turn.id === id);
        });
      };
    },
    getTurnByTurnId() {
      return (id: string) => {
        const lineInfo = this.getLineInfoByTurnId(id);
        return lineInfo?.turns.find((turn) => turn.id === id);
      };
    },
    getBusStopById() {
      return (id: string) => {
        return this.busStops.find((busStop) => busStop.id === id);
      };
    },
    getFavoriteLine(state) {
      return (
        turnId: string,
        getOnBusStopId?: string,
        getOffBusStopId?: string
      ) => {
        return state.favoriteLines.find(
          (favoriteLine) =>
            favoriteLine.turnId === turnId &&
            favoriteLine.getOnBusStop?.id === getOnBusStopId &&
            favoriteLine.getOffBusStop?.id === getOffBusStopId
        );
      };
    },
    getFavoriteLineIndex(state) {
      return (
        turnId: string,
        getOnBusStopId?: string,
        getOffBusStopId?: string
      ) => {
        return state.favoriteLines.findIndex(
          (favoriteLine) =>
            favoriteLine.turnId === turnId &&
            favoriteLine.getOnBusStop?.id === getOnBusStopId &&
            favoriteLine.getOffBusStop?.id === getOffBusStopId
        );
      };
    },
    getNonBookableFavoriteLine(state) {
      return (turnId: string) => {
        return state.favoriteLines.find(
          (favoriteLine) => favoriteLine.turnId === turnId
        );
      };
    },
    getNonBookableFavoriteLineIndex(state) {
      return (turnId: string) => {
        return state.favoriteLines.findIndex(
          (favoriteLine) => favoriteLine.turnId === turnId
        );
      };
    },
    hasFavoriteLine(state) {
      return (
        turnId: string,
        getOnBusStopId?: string,
        getOffBusStopId?: string
      ) => {
        return (
          state.favoriteLines.filter((favoriteLine) => {
            return (
              favoriteLine.turnId === turnId &&
              favoriteLine.getOnBusStop?.id === getOnBusStopId &&
              favoriteLine.getOffBusStop?.id === getOffBusStopId
            );
          }).length > 0
        );
      };
    },
    getBookRequiredType(state) {
      return () => {
        let hasBookRequired = false;
        let hasNoBookRequired = false;

        for (const lineInfo of state.lineInfos) {
          if (lineInfo.turns.every((turn) => turn.bookYn)) {
            hasBookRequired = true;
          } else if (lineInfo.turns.every((turn) => !turn.bookYn)) {
            hasNoBookRequired = true;
          } else {
            return BookRequiredType.MIXED;
          }
        }

        if (hasBookRequired && !hasNoBookRequired) {
          return BookRequiredType.BOOK_REQUIRED;
        } else if (!hasBookRequired && hasNoBookRequired) {
          return BookRequiredType.NO_BOOK_REQUIRED;
        } else {
          return BookRequiredType.MIXED;
        }
      };
    },
    getTurnUseBeginDtm() {
      return (turnId: string) => {
        return this.getTurnByTurnId(turnId)?.useBeginDtm;
      };
    },
  },
  actions: {
    clearFavoriteLines() {
      this.favoriteLines = [];
    },

    /** 탑승 데이터를 즐겨찾기 노선으로 변환하여 즐겨찾기 목록을 업데이트 한다. */
    addOnBoardLineIntoFavoriteLine(onBoardLine: IOnBoardLine) {
      let parsedOnBoardLine = parseOnBoardToFavorite(
        onBoardLine,
        this.favoriteLines
      );

      const { getOnBusStop, getOffBusStop, turnId } = parsedOnBoardLine;

      const isBookable = !!parsedOnBoardLine.bookIntervals?.length;

      const index = isBookable
        ? this.getFavoriteLineIndex(turnId, getOnBusStop?.id, getOffBusStop?.id)
        : this.getNonBookableFavoriteLineIndex(turnId);

      // 실제 즐겨찾기에 존재한다면, isTempOnboardFavorite가 아님
      const isActuallyFavorited = index !== -1;
      if (isActuallyFavorited) {
        parsedOnBoardLine = {
          ...this.favoriteLines[index],
          isBoarding: true,
          isTempOnboardFavorite: false,
          boardUid: parsedOnBoardLine.boardUid,
          boardedCarNo: parsedOnBoardLine.boardedCarNo,
          requestGetOffBusStopId: parsedOnBoardLine.requestGetOffBusStopId,
        };
        this.favoriteLines[index] = { ...parsedOnBoardLine };
      }

      this.$patch((state) => {
        if (isActuallyFavorited) {
          // 기존 인덱스를 유지하되 해당 항목만 갱신
          state.favoriteLines.splice(index, 1, {
            ...parsedOnBoardLine,
          });
        } else {
          // 기존에 없던 경우 최상단에 추가
          state.favoriteLines.unshift({ ...parsedOnBoardLine });
        }
      });
    },

    updateFavoriteLineState(favoriteLine: IFavoriteLine) {
      this.$patch((state) => {
        const idx = state.favoriteLines.findIndex(
          (line) => line.turnId === favoriteLine.turnId
        );
        if (idx === -1) {
          state.favoriteLines.unshift({ ...favoriteLine });
        } else {
          state.favoriteLines[idx] = { ...favoriteLine };
        }
      });
    },

    removeFavoriteLineState(
      turnId: string,
      onStationId?: string,
      offStationId?: string
    ) {
      this.$patch((state) => {
        state.favoriteLines = state.favoriteLines.filter(
          (line) =>
            !(
              line.turnId === turnId &&
              line.getOnBusStop?.id === onStationId &&
              line.getOffBusStop?.id === offStationId
            )
        );
      });
    },

    /**
     * 즐겨찾기된 노선 중 isBoarding=true인 항목을 모두 false로 바꾼다.
     */
    clearOnBoardingLine() {
      this.$patch((state) => {
        const updated = state.favoriteLines.map((line) => ({
          ...line,
          isBoarding: false,
        }));
        state.favoriteLines = updated;
      });
    },

    // API 호출을 실행하는 별도 메소드
    async executeGetAllLineInfosApi(
      sortOrder?: string
    ): Promise<IResultData<ILineInfo[]>> {
      const lineInfosResult: IResultData<ILineInfo[]> = {
        isSuccess: false,
        data: [],
      };

      await lineInfoRepository()
        .getAllLineInfos(sortOrder)
        .then((result) => {
          result
            .map((lineInfos) => {
              lineInfosResult.isSuccess = true;
              lineInfosResult.data = lineInfos;
              this.busStops = parseBusStop(lineInfos);
              // 성공적으로 데이터를 가져온 경우 캐시에 저장
              this.saveLineInfosToCache(lineInfos);
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(lineInfosResult, error);
              lineInfosResult.errorMessage = error.message;

              // 네트워크 오류시 만료된 캐시라도 사용
              const expiredCache = this.getExpiredLineInfosFromCache();
              if (expiredCache && expiredCache.data) {
                lineInfosResult.isSuccess = true;
                lineInfosResult.data = expiredCache.data;
                this.busStops = parseBusStop(expiredCache.data);
              }
            });
        })
        .catch((error) => {
          lineInfosResult.errorMessage = error.message;

          // 네트워크 오류시 만료된 캐시라도 사용
          const expiredCache = this.getExpiredLineInfosFromCache();
          if (expiredCache && expiredCache.data) {
            lineInfosResult.isSuccess = true;
            lineInfosResult.data = expiredCache.data;
            this.busStops = parseBusStop(expiredCache.data);
          }
        });

      this.lineInfos = lineInfosResult.data ?? [];
      return lineInfosResult;
    },

    async getAllLineInfos(
      sortOrder?: string
    ): Promise<IResultData<ILineInfo[]>> {
      // 1. 캐시 확인 (기존 로직 유지)
      const cachedResult = this.getLineInfosFromCache();
      if (cachedResult && cachedResult.data) {
        // 캐시가 있으면 바로 반환
        this.lineInfos = cachedResult.data;
        this.busStops = parseBusStop(cachedResult.data);
        return cachedResult;
      }

      // 2. 진행 중인 요청이 있는지 확인
      if (this.pendingGetAllLineInfosRequest) {
        return await this.pendingGetAllLineInfosRequest;
      }

      // 3. 새로운 API 요청 시작
      const apiCall = this.executeGetAllLineInfosApi(sortOrder);
      this.pendingGetAllLineInfosRequest = apiCall;

      try {
        const result = await apiCall;
        return result;
      } finally {
        // 4. 요청 완료 후 pendingRequest 초기화
        this.pendingGetAllLineInfosRequest = null;
      }
    },

    // 캐시에서 유효한 데이터 조회
    getLineInfosFromCache(): IResultData<ILineInfo[]> | null {
      try {
        const cacheKey = "lineInfos_cache";
        const cached = getDecompressedCacheData(cacheKey);
        if (!cached) return null;

        const cacheData = JSON.parse(cached);
        const now = Date.now();

        // 캐시가 유효한지 확인 (설정된 TTL 기준)
        if (now < cacheData.expiresAt) {
          // JSON.parse로 가져온 plain object를 LineInfo 클래스 인스턴스로 복원
          const restoredLineInfos = this.restoreLineInfosFromPlainObjects(
            cacheData.data
          );
          return {
            isSuccess: true,
            data: restoredLineInfos,
          };
        }

        return null;
      } catch (error) {
        console.error("캐시 조회 오류:", error);
        return null;
      }
    },

    // 만료된 캐시도 포함하여 조회 (네트워크 오류시 사용)
    getExpiredLineInfosFromCache(): IResultData<ILineInfo[]> | null {
      try {
        const cacheKey = "lineInfos_cache";
        const cached = getDecompressedCacheData(cacheKey);
        if (!cached) return null;

        const cacheData = JSON.parse(cached);
        // JSON.parse로 가져온 plain object를 LineInfo 클래스 인스턴스로 복원
        const restoredLineInfos = this.restoreLineInfosFromPlainObjects(
          cacheData.data
        );
        return {
          isSuccess: true,
          data: restoredLineInfos,
        };
      } catch (error) {
        console.error("만료된 캐시 조회 오류:", error);
        return null;
      }
    },

    // Plain object를 LineInfo 클래스 인스턴스로 복원
    restoreLineInfosFromPlainObjects(plainObjects: unknown[]): ILineInfo[] {
      if (!Array.isArray(plainObjects)) {
        console.error("LineInfo 복원 오류: 배열이 아닌 데이터:", plainObjects);
        return [];
      }

      return plainObjects.map((plainObj) => {
        try {
          // 기본 유효성 검사
          if (!plainObj || typeof plainObj !== "object") {
            throw new Error("Invalid object structure");
          }

          // LineInfo 클래스 인스턴스로 복원
          const lineInfo = new LineInfo(plainObj as any);
          return lineInfo;
        } catch (error) {
          console.error("LineInfo 복원 중 오류:", error, plainObj);
          // 복원 실패시 원본 객체 반환 (타입 캐스팅)
          return plainObj as ILineInfo;
        }
      });
    },

    // 캐시에 데이터 저장
    saveLineInfosToCache(lineInfos: ILineInfo[]): void {
      try {
        const cacheKey = "lineInfos_cache";
        const now = Date.now();
        const ttlMillis = getCacheTtlMillis();
        const cacheData = {
          data: lineInfos,
          timestamp: now,
          expiresAt: now + ttlMillis,
        };

        // 데이터 압축
        const json = JSON.stringify(cacheData);
        const compressed = compressToUTF16(json);

        localStorage.setItem(cacheKey, compressed);
      } catch (error) {
        console.error("캐시 저장 오류:", error);
      }
    },

    // 캐시 만료 확인
    isCacheExpired(): boolean {
      try {
        const cacheKey = "lineInfos_cache";
        const cached = getDecompressedCacheData(cacheKey);
        if (!cached) return true;

        const cacheData = JSON.parse(cached);
        const now = Date.now();

        return now >= cacheData.expiresAt;
      } catch (error) {
        console.error("캐시 만료 확인 오류:", error);
        return true;
      }
    },

    // Lite 캐시에서 유효한 데이터 조회
    getLineInfosLiteFromCache(): IResultData<ILineInfoLite[]> | null {
      try {
        const cacheKey = "lineInfosLite_cache";
        const cached = getDecompressedCacheData(cacheKey);
        if (!cached) return null;

        const cacheData = JSON.parse(cached);
        const now = Date.now();

        // 캐시가 유효한지 확인
        if (now < cacheData.expiresAt) {
          return {
            isSuccess: true,
            data: cacheData.data,
          };
        }

        return null;
      } catch (error) {
        console.error("Lite 캐시 조회 오류:", error);
        return null;
      }
    },

    // Lite 만료된 캐시도 포함하여 조회
    getExpiredLineInfosLiteFromCache(): IResultData<ILineInfoLite[]> | null {
      try {
        const cacheKey = "lineInfosLite_cache";
        const cached = getDecompressedCacheData(cacheKey);
        if (!cached) return null;

        const cacheData = JSON.parse(cached);
        return {
          isSuccess: true,
          data: cacheData.data,
        };
      } catch (error) {
        console.error("만료된 Lite 캐시 조회 오류:", error);
        return null;
      }
    },

    // Lite 캐시에 데이터 저장
    saveLineInfosLiteToCache(lineInfosLite: ILineInfoLite[]): void {
      try {
        const cacheKey = "lineInfosLite_cache";
        const now = Date.now();
        const ttlMillis = getCacheTtlMillis();
        const cacheData = {
          data: lineInfosLite,
          timestamp: now,
          expiresAt: now + ttlMillis,
        };

        const json = JSON.stringify(cacheData);
        const compressed = compressToUTF16(json);

        localStorage.setItem(cacheKey, compressed);
      } catch (error) {
        console.error("Lite 캐시 저장 오류:", error);
      }
    },

    // Lite API 호출을 실행하는 별도 메소드
    async executeGetAllLineInfosLiteApi(
      sortOrder?: string
    ): Promise<IResultData<ILineInfoLite[]>> {
      const lineInfosResult: IResultData<ILineInfoLite[]> = {
        isSuccess: false,
        data: [],
      };

      await lineInfoRepository()
        .getAllLineInfosLite(sortOrder)
        .then((result) => {
          result
            .map((lineInfos) => {
              lineInfosResult.isSuccess = true;
              lineInfosResult.data = lineInfos;
              // 성공적으로 데이터를 가져온 경우 캐시에 저장
              this.saveLineInfosLiteToCache(lineInfos);
              // busStopIdToLineInfos 저장을 별도 메서드로 분리
              this.setBusStopIdToLineInfos(lineInfos);
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(lineInfosResult, error);
              lineInfosResult.errorMessage = error.message;

              // 네트워크 오류시 만료된 캐시라도 사용
              const expiredCache = this.getExpiredLineInfosLiteFromCache();
              if (expiredCache && expiredCache.data) {
                lineInfosResult.isSuccess = true;
                lineInfosResult.data = expiredCache.data;
                // busStopIdToLineInfos 저장을 별도 메서드로 분리 (캐시 데이터로)
                this.setBusStopIdToLineInfos(expiredCache.data);
              }
            });
        })
        .catch((error) => {
          lineInfosResult.errorMessage = error.message;

          // 네트워크 오류시 만료된 캐시라도 사용
          const expiredCache = this.getExpiredLineInfosLiteFromCache();
          if (expiredCache && expiredCache.data) {
            lineInfosResult.isSuccess = true;
            lineInfosResult.data = expiredCache.data;
            // busStopIdToLineInfos 저장을 별도 메서드로 분리 (캐시 데이터로)
            this.setBusStopIdToLineInfos(expiredCache.data);
          }
        });

      this.lineInfosLite = lineInfosResult.data ?? [];
      return lineInfosResult;
    },

    setBusStopIdToLineInfos(lineInfos: ILineInfoLite[]) {
      const map = new Map<string, ILineInfoLite[]>();
      lineInfos.forEach((lineInfo) => {
        lineInfo.turns.forEach((turn) => {
          turn.stations?.forEach((station) => {
            if (!map.has(station.id)) {
              map.set(station.id, []);
            }
            map.get(station.id)!.push(lineInfo);
          });
        });
      });
      this.busStopIdToLineInfos = map;
    },

    async getAllLineInfosLite(
      sortOrder?: string
    ): Promise<IResultData<ILineInfoLite[]>> {
      // 1. 캐시 확인
      const cachedResult = this.getLineInfosLiteFromCache();
      if (cachedResult && cachedResult.data) {
        // 캐시가 있으면 바로 반환
        this.lineInfosLite = cachedResult.data;
        return cachedResult;
      }

      // 2. 진행 중인 요청이 있는지 확인
      if (this.pendingGetAllLineInfosLiteRequest) {
        return await this.pendingGetAllLineInfosLiteRequest;
      }

      // 3. 새로운 API 요청 시작
      const apiCall = this.executeGetAllLineInfosLiteApi(sortOrder);
      this.pendingGetAllLineInfosLiteRequest = apiCall;

      try {
        const result = await apiCall;
        return result;
      } finally {
        // 4. 요청 완료 후 pendingRequest 초기화
        this.pendingGetAllLineInfosLiteRequest = null;
      }
    },

    async getAllLineInfosLiteByFilters(
      query: string,
      sortOrder?: string
    ): Promise<IResultData<ILineInfoLite[]>> {
      const lineInfosResult: IResultData<ILineInfoLite[]> = {
        isSuccess: false,
        data: [],
      };

      await lineInfoRepository()
        .getAllLineInfosLiteByFilters(query, sortOrder)
        .then((result) => {
          result
            .map((lineInfos) => {
              lineInfosResult.isSuccess = true;
              lineInfosResult.data = lineInfos;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(lineInfosResult, error);
              lineInfosResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          lineInfosResult.errorMessage = error.message;
        });

      return lineInfosResult;
    },

    async getLineInfosByFilters(
      query: string,
      sortOrder?: string
    ): Promise<IResultData<ILineInfo[]>> {
      const lineInfosResult: IResultData<ILineInfo[]> = {
        isSuccess: false,
        data: [],
      };

      await lineInfoRepository()
        .getLineInfosByFilters(query, sortOrder)
        .then((result) => {
          result
            .map((lineInfos) => {
              lineInfosResult.isSuccess = true;
              lineInfosResult.data = lineInfos;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(lineInfosResult, error);
              lineInfosResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          lineInfosResult.errorMessage = error.message;
        });

      return lineInfosResult;
    },

    async getLineInfoDetail(routeId: string): Promise<IResultData<ILineInfo>> {
      const lineInfoResult: IResultData<ILineInfo> = {
        isSuccess: false,
      };

      await lineInfoRepository()
        .getLineInfoDetail(routeId)
        .then((result) => {
          result
            .map((lineInfo) => {
              lineInfoResult.isSuccess = true;
              lineInfoResult.data = lineInfo;
              this.selectedLineInfoDetail = lineInfo;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(lineInfoResult, error);
              lineInfoResult.errorMessage = error.message;
              this.selectedLineInfoDetail = undefined;
            });
        })
        .catch((error) => {
          lineInfoResult.errorMessage = error.message;
          this.selectedLineInfoDetail = undefined;
        });

      return lineInfoResult;
    },

    async getLineTypes(): Promise<IResultData<ILineType[]>> {
      const lineTypesResult: IResultData<ILineType[]> = {
        isSuccess: false,
        data: [],
      };

      await lineInfoRepository()
        .getLineTypes()
        .then((result) => {
          result
            .map((lineTypes) => {
              lineTypesResult.isSuccess = true;
              lineTypesResult.data = lineTypes;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(lineTypesResult, error);
              lineTypesResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          lineTypesResult.errorMessage = error.message;
        });

      this.lineTypes = lineTypesResult.data ?? [];
      return lineTypesResult;
    },

    async getLineDistricts(
      lineTypeCode?: LineTypeCode
    ): Promise<IResultData<ILineDistrict[]>> {
      const lineDistrictsResult: IResultData<ILineDistrict[]> = {
        isSuccess: false,
        data: [],
      };

      await lineInfoRepository()
        .getLineDistricts(lineTypeCode)
        .then((result) => {
          result
            .map((lineDistricts) => {
              lineDistrictsResult.isSuccess = true;
              lineDistrictsResult.data = lineDistricts;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(lineDistrictsResult, error);
              lineDistrictsResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          lineDistrictsResult.errorMessage = error.message;
        });

      this.lineDistricts = lineDistrictsResult.data ?? [];
      return lineDistrictsResult;
    },

    async getLineTurns(searchParms: string): Promise<IResultData<ITurn[]>> {
      const turnsResult: IResultData<ITurn[]> = {
        isSuccess: false,
        data: [],
      };

      await lineInfoRepository()
        .getLineTurns(searchParms)
        .then((result) => {
          result
            .map((lineDistricts) => {
              turnsResult.isSuccess = true;
              turnsResult.data = lineDistricts;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(turnsResult, error);
              turnsResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          turnsResult.errorMessage = error.message;
        });

      this.turns = turnsResult.data ?? [];
      return turnsResult;
    },

    async getNearBusStops(
      latitude: number,
      longitude: number,
      radiusMeters: number
    ): Promise<IResultData<IBusStop[]>> {
      const nearBusStopsResult: IResultData<IBusStop[]> = {
        isSuccess: false,
        data: [],
      };

      await lineInfoRepository()
        .getNearBusStops(latitude, longitude, radiusMeters)
        .then((result) => {
          result
            .map((busStops) => {
              nearBusStopsResult.isSuccess = true;
              nearBusStopsResult.data = busStops;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(nearBusStopsResult, error);
              nearBusStopsResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          nearBusStopsResult.errorMessage = error.message;
        });

      this.nearBusStops = nearBusStopsResult.data ?? [];
      return nearBusStopsResult;
    },

    async getFavoriteLines(): Promise<IResultData<IFavoriteLine[]>> {
      const favoriteLinesResult: IResultData<IFavoriteLine[]> = {
        isSuccess: false,
        data: [],
      };

      await lineInfoRepository()
        .getFavoriteLineInfos()
        .then((result) => {
          result
            .map((favoriteLines) => {
              favoriteLinesResult.isSuccess = true;

              // 서비스 정보에서 정렬 조건 가져오기 (기본값: "lineStartTime asc, lineNum asc, routeName asc")
              const serviceStore = useServiceStore();
              const sortOrder =
                serviceStore.serviceInfo?.favoriteLinesSortOrder ||
                "lineStartTime asc, lineNum asc, routeName asc";

              favoriteLinesResult.data = sortArrayByString(
                favoriteLines,
                sortOrder
              );
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(favoriteLinesResult, error);
              favoriteLinesResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          favoriteLinesResult.errorMessage = error.message;
        });

      this.favoriteLines = favoriteLinesResult.data
        ? [...favoriteLinesResult.data]
        : [];
      return favoriteLinesResult;
    },

    async addFavoriteLine(
      favoriteLine: IFavoriteLine,
      getOnTargetDate?: string,
      getOffTargetDate?: string
    ): Promise<IResultData<boolean>> {
      const addFavoriteLineResult: IResultData<boolean> = {
        isSuccess: false,
        data: false,
      };

      await lineInfoRepository()
        .addFavoriteLine(favoriteLine, getOnTargetDate, getOffTargetDate)
        .then((result) => {
          result
            .map((resData) => {
              this.updateFavoriteLineState(favoriteLine);
              addFavoriteLineResult.isSuccess = resData.isSuccess;
              addFavoriteLineResult.data = resData.isSuccess;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(addFavoriteLineResult, error);
              addFavoriteLineResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          addFavoriteLineResult.errorMessage = error.message;
        });

      return addFavoriteLineResult;
    },

    async removeFavoriteLine(
      turnId: string,
      onStationId?: string,
      offStationId?: string
    ): Promise<IResultData<boolean>> {
      const removeFavoriteLineResult: IResultData<boolean> = {
        isSuccess: false,
        data: false,
      };

      await lineInfoRepository()
        .removeFavoriteLine(turnId, onStationId, offStationId)
        .then((result) => {
          result
            .map((resData) => {
              this.removeFavoriteLineState(turnId, onStationId, offStationId);
              removeFavoriteLineResult.isSuccess = resData.isSuccess;
              removeFavoriteLineResult.data = resData.isSuccess;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(removeFavoriteLineResult, error);
              removeFavoriteLineResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          removeFavoriteLineResult.errorMessage = error.message;
        });

      return removeFavoriteLineResult;
    },

    async getBusLocations(
      routeId: string,
      turnId?: string,
      allocUid?: string
    ): Promise<IResultData<IBusLocation[]>> {
      const busLocationResult: IResultData<IBusLocation[]> = {
        isSuccess: false,
      };

      await lineInfoRepository()
        .getBusLocations(routeId, turnId, allocUid)
        .then((result) => {
          result
            .map((location) => {
              busLocationResult.isSuccess = true;
              busLocationResult.data = location;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(busLocationResult, error);
              busLocationResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          busLocationResult.errorMessage = error.message;
        });

      this.busLocations = busLocationResult.data;
      return busLocationResult;
    },

    updateIsBusRunning(isRunning: boolean) {
      this.isBusRunning = isRunning;
    },

    async updatePushStatusOfFavoriteLine(
      favoriteLine: IFavoriteLineData
    ): Promise<IResultData<boolean>> {
      const pushStatusResult: IResultData<boolean> = {
        isSuccess: false,
        data: false,
      };

      await lineInfoRepository()
        .addFavoriteLine(favoriteLine)
        .then((result) => {
          result
            .map((resData) => {
              pushStatusResult.isSuccess = resData.isSuccess;
              pushStatusResult.data = resData.isSuccess;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(pushStatusResult, error);
              pushStatusResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          pushStatusResult.errorMessage = error.message;
        });

      return pushStatusResult;
    },

    async getBookIntervals(
      turnId?: string
    ): Promise<IResultData<IBookInterval[]>> {
      return getBookIntervals().execute(turnId);
    },

    async getLineFare(turnId: string): Promise<IResultData<ILineFare>> {
      return lineFareUseCase().getLineFare(turnId);
    },

    async getOnBoardLine(): Promise<IResultData<IOnBoardLineData>> {
      const onBoardLineResult: IResultData<IOnBoardLineData> = {
        isSuccess: false,
      };

      await lineInfoRepository()
        .getOnBoardLine()
        .then((result) => {
          result
            .map((onBoardLineData) => {
              onBoardLineResult.isSuccess = true;
              onBoardLineResult.data = onBoardLineData;

              // 탑승 노선이 있는 경우
              if (onBoardLineData.onBoardLine) {
                this.addOnBoardLineIntoFavoriteLine(
                  onBoardLineData.onBoardLine
                );
              } else {
                this.clearOnBoardingLine();
              }
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(onBoardLineResult, error);
              onBoardLineResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          onBoardLineResult.errorMessage = error.message;
        });

      return onBoardLineResult;
    },

    async getFilter<TResponse = any>(
      pathParam: string,
      key: string,
      type: FilterKeyValueType,
      optionMapper: OptionMapper<TResponse>,
      isPublic: boolean,
      multiple?: boolean,
      label?: string,
      dummyOptionName?: string,
      defaultOptionCode?: string,
      parentKey?: string,
      essential?: boolean,
      callback?: (selectedOptions?: IOption[] | IOption) => void,
      getOptions?: GetOptions<TResponse>,
      allowDirectInput?: boolean
    ): Promise<IResultData<IFilter>> {
      const filterData: IResultData<IFilter> = {
        isSuccess: false,
      };

      await lineInfoRepository()
        .getFilter(
          pathParam,
          key,
          type,
          optionMapper,
          isPublic,
          multiple,
          label,
          dummyOptionName,
          defaultOptionCode,
          parentKey,
          essential,
          callback,
          getOptions,
          allowDirectInput
        )
        .then((result) => {
          result
            .map((resData) => {
              filterData.isSuccess = true;
              filterData.data = resData;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(filterData, error);
              filterData.errorMessage = error.message;
            });
        })
        .catch((error) => {
          filterData.errorMessage = error.message;
        });

      return filterData;
    },

    async getBusStopLayoutInfo(
      turnUid: string,
      busStopUid: string
    ): Promise<IResultData<IBusStopLayoutInfo>> {
      const layoutInfoResult: IResultData<IBusStopLayoutInfo> = {
        isSuccess: false,
      };

      await lineInfoRepository()
        .getBusStopLayoutInfo(turnUid, busStopUid)
        .then((result) => {
          result
            .map((layoutInfo) => {
              layoutInfoResult.isSuccess = true;
              layoutInfoResult.data = layoutInfo;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(layoutInfoResult, error);
              layoutInfoResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          layoutInfoResult.errorMessage = error.message;
        });

      return layoutInfoResult;
    },

    async getContactList(): Promise<IResultData<IContact[]>> {
      const contactListResult: IResultData<IContact[]> = {
        isSuccess: false,
        data: [],
      };

      await lineInfoRepository()
        .getContactList()
        .then((result) => {
          result
            .map((contacts) => {
              contactListResult.isSuccess = true;
              contactListResult.data = contacts;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(contactListResult, error);
              contactListResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          contactListResult.errorMessage = error.message;
        });

      return contactListResult;
    },

    async createTmapMultipleViaPointGuide(
      busLocation: IBusLocation
    ): Promise<IResultData<IArrivalTime[]>> {
      const tmapResult: IResultData<IArrivalTime[]> = {
        isSuccess: false,
      };

      await lineInfoRepository()
        .createTmapMultipleViaPointsGuide(busLocation)
        .then((result) => {
          result
            .map((resData) => {
              tmapResult.isSuccess = true;
              tmapResult.data = resData;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(tmapResult, error);
              tmapResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          tmapResult.errorMessage = error.message;
        });

      return tmapResult;
    },

    /** 과거도착시각, 평균도착시각 받아오는 API */
    async getBusStopArrivalTimes(
      lineTurnUid: string,
      busOrder: number,
      driveDate: string,
      type: ArrivalTimeOptionCodeType
    ): Promise<IResultData<IArrivalTime[]>> {
      const busStopListResult: IResultData<IArrivalTime[]> = {
        isSuccess: false,
        data: [],
      };

      if (
        type !== ArrivalTimeOptionCode.AVG &&
        type !== ArrivalTimeOptionCode.PAST
      ) {
        return {
          isSuccess: false,
          errorMessage: "이 API는 평균 또는 과거 도착시각 조회만 가능",
        };
      }

      await lineInfoRepository()
        .getBusStopArrivalTimes(lineTurnUid, busOrder, driveDate, type)
        .then((result) => {
          result
            .map((resData) => {
              busStopListResult.isSuccess = true;
              busStopListResult.data = resData;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(busStopListResult, error);
              busStopListResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          busStopListResult.errorMessage = error.message;
        });

      return busStopListResult;
    },

    async getFavoriteBusStops(): Promise<IResultData<IFavoriteBusStop[]>> {
      const favoriteBusStopsResult: IResultData<IFavoriteBusStop[]> = {
        isSuccess: false,
        data: [],
      };

      await lineInfoRepository()
        .getFavoriteBusStops()
        .then((result) => {
          result
            .map((favoriteBusStops) => {
              favoriteBusStopsResult.isSuccess = true;
              favoriteBusStopsResult.data = favoriteBusStops;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(favoriteBusStopsResult, error);
              favoriteBusStopsResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          favoriteBusStopsResult.errorMessage = error.message;
        });

      this.favoriteBusStops = favoriteBusStopsResult.data
        ? [...favoriteBusStopsResult.data]
        : [];
      return favoriteBusStopsResult;
    },

    async addFavoriteBusStop(
      favoriteBusStop: IFavoriteBusStop
    ): Promise<IResultData<boolean>> {
      const addResult: IResultData<boolean> = {
        isSuccess: false,
        data: false,
      };

      await lineInfoRepository()
        .addFavoriteBusStop(favoriteBusStop)
        .then((result) => {
          result
            .map((resData) => {
              this.favoriteBusStops.push(favoriteBusStop);
              addResult.isSuccess = resData.isSuccess;
              addResult.data = resData.isSuccess;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(addResult, error);
              addResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          addResult.errorMessage = error.message;
        });

      return addResult;
    },

    async removeFavoriteBusStop(
      busStopId: string
    ): Promise<IResultData<boolean>> {
      const removeResult: IResultData<boolean> = {
        isSuccess: false,
        data: false,
      };

      await lineInfoRepository()
        .removeFavoriteBusStop(busStopId)
        .then((result) => {
          result
            .map((resData) => {
              this.favoriteBusStops = this.favoriteBusStops.filter(
                (busStop) => !(busStop.uid === busStopId)
              );
              removeResult.isSuccess = resData.isSuccess;
              removeResult.data = resData.isSuccess;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(removeResult, error);
              removeResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          removeResult.errorMessage = error.message;
        });

      return removeResult;
    },

    /**
     * 교통정보 목록 조회 API - 전체 조회시 offset, limit 없이 조회할것
     * @param routeUid 노선 식별자
     * @param offset 조회 시작 위치 (for 페이지네이션)
     * @param limit 한 번에 조회할 데이터 수 (for 페이지네이션)
     */
    async getRoadEventList(
      routeUid: string,
      offset?: number,
      limit?: number
    ): Promise<IResultData<IRoadEventsCollection>> {
      const roadEventListResult: IResultData<IRoadEventsCollection> = {
        isSuccess: false,
        data: undefined,
      };

      await lineInfoRepository()
        .getRoadEventList(routeUid, offset, limit)
        .then((result) => {
          result
            .map((contacts) => {
              roadEventListResult.isSuccess = true;
              roadEventListResult.data = contacts;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(roadEventListResult, error);
              roadEventListResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          roadEventListResult.errorMessage = error.message;
        });

      return roadEventListResult;
    },

    async getFullSeatLineList(): Promise<IResultData<IFullSeatLine[]>> {
      const fullSeatLineListResult: IResultData<IFullSeatLine[]> = {
        isSuccess: false,
        data: undefined,
      };

      await lineInfoRepository()
        .getFullSeatLineList()
        .then((result) => {
          result
            .map((resData) => {
              fullSeatLineListResult.isSuccess = true;
              fullSeatLineListResult.data = resData;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(fullSeatLineListResult, error);
              fullSeatLineListResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          fullSeatLineListResult.errorMessage = error.message;
        });

      return fullSeatLineListResult;
    },

    async getLineSearchFilter(
      searchParams: string
    ): Promise<IResultData<ILineSearchFilter[]>> {
      const lineSearchFilterResult: IResultData<ILineSearchFilter[]> = {
        isSuccess: false,
        data: undefined,
      };

      await lineInfoRepository()
        .getLineSearchFilter(searchParams)
        .then((result) => {
          result
            .map((resData) => {
              lineSearchFilterResult.isSuccess = true;
              lineSearchFilterResult.data = resData;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(lineSearchFilterResult, error);
              lineSearchFilterResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          lineSearchFilterResult.errorMessage = error.message;
        });
      return lineSearchFilterResult;
    },
  },
});
